import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { FilterGroup } from '../../models';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

/**
 * FilterGroup — Presentation Component
 *
 * Renders a single collapsible filter section with checkboxes.
 * Supports:
 * - Accordion expand/collapse
 * - Server-side search within filter options
 * - Server-side "Show more" / "عرض المزيد" pagination
 *
 * Emits events upward — does NOT modify state directly.
 */
@Component({
  selector: 'app-filter-group',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './filter-group.component.html',
  styleUrl: './filter-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterGroupComponent implements OnInit, OnDestroy, OnChanges {
  /** The filter group configuration and state */
  @Input({ required: true }) group!: FilterGroup;

  /** Emitted when a checkbox is toggled — emits the option value */
  @Output() toggle = new EventEmitter<string>();

  /** Emitted when the user types in the search box */
  @Output() search = new EventEmitter<string>();

  /** Emitted when the user clicks "Show More" */
  @Output() loadMore = new EventEmitter<void>();

  /** Local UI state: search text within this filter group */
  filterSearch = '';

  private searchSubject$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Debounce search input to avoid spamming the API
    this.searchSubject$.pipe(
      takeUntil(this.destroy$),
      debounceTime(400)
    ).subscribe(term => {
      this.search.emit(term);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group']) {
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(term: string): void {
    this.searchSubject$.next(term);
  }

  onToggle(value: string): void {
    this.toggle.emit(value);
    this.cdr.markForCheck();
  }

  onItemClick(event: MouseEvent, value: string): void {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() === 'input') {
      return;
    }
    event.preventDefault();
    this.onToggle(value);
  }

  onLoadMore(): void {
    this.loadMore.emit();
  }

  isSelected(value: string): boolean {
    if (!this.group || !this.group.selectedValues || !Array.isArray(this.group.selectedValues)) {
      return false;
    }
    const target = String(value ?? '').trim();
    return this.group.selectedValues.some(val => String(val ?? '').trim() === target);
  }
}
